import { SportDataGateWayRetriever, initialSportsDataFeed } from "./SportDataGateWayRetriever";
import { EventPushResponse } from "sbtech-sports-data-api-client";
import { IPersonalizationMethodResponse, IEventsExtendedData, IEventsExtendedDataResponse } from "./Interfaces";
import  * as Interfaces from "./Interfaces";
import { observe, Lambda, observable, action, IObjectDidChange } from "mobx";
import { Time, OddStyles, UserInfo } from "sb-resp-lib";
import { DeviceChangeDispatcherMock } from "sb-resp-lib/lib/mocks";
import { BaseDict } from "../blocks/PersonalizedHighlights/dict";
import { buildEventsExtendedDataParams } from "./DataParamHelpers";

export type DataUtilsType = {
    betSlipUtil: BetSlipUtil;
};

export type StatisticsContentProvider = {
    factory: Interfaces.IStatisticsProviderFactory,
    useStatistics: boolean,
    betRadarClientId: number
}

export type DataFacadeDataComposite = {
    pageMethods: PersonalizedHighlightsPagemethods,
    sportDataGatewayRetriever: SportDataGateWayRetriever,
    statisticsProvider: StatisticsContentProvider,
    cms: Interfaces.CmsDataFormat,
    utils: DataUtilsType
    navHandler: Application;
    timeControl: TimeControl;
    userInfo: UserInfo,
    currentOddStyleIndex : number,
    deviceChangeDispatcher?: DeviceChangeDispatcherMock,
    blockUniqueId: string,
    dict: BaseDict
};

/*
    The class has 'istanbul ignore' flag , because of testing typescript interface with Jest.
    It`s known issue : (https://github.com/kulshekhar/ts-jest/issues/378)
    The class is fully tested, but propery "sportsDataChangeFeed" implement interface "ChangeFeedUpdate" which is not
    testable with Jest - there is no types like in typescript
 */

/* istanbul ignore next */
export class DataFacade {

    deviceChangeDispatcher: DeviceChangeDispatcherMock | undefined;
    cms: Interfaces.CmsDataFormat;
    @observable dataCollection: EventPushResponse | undefined;
    @observable personalizationDataResponse: IPersonalizationMethodResponse;
    @observable eventsExtendedData: IEventsExtendedData[];
    pageMethods: PersonalizedHighlightsPagemethods;
    AuthorizationToken: string | undefined;
    sportDataGateway: SportDataGateWayRetriever;
    statisticsProvider: StatisticsContentProvider;
    disposeSportsDataFeed: Lambda;
    disposeRemoveOfEvents!: Lambda;
    @observable shouldRemoveEvents: boolean;
    disposeTimeZone: Lambda;
    betSlipUtil: BetSlipUtil;
    navHandler: Application;
    @observable currentTimeZone: number;
    @observable currentOddStyle: string;
    eventsManager: EventsManager;
    timeControl: TimeControl;
    timeInstance: Time;
    userInfo: UserInfo;
    dict: BaseDict;

    constructor(options: DataFacadeDataComposite) {

        this.shouldRemoveEvents = false;
        this.AuthorizationToken = undefined;
        this.dataCollection = initialSportsDataFeed;
        this.pageMethods = options.pageMethods;
        this.personalizationDataResponse = { BranchGroups: [] };
        this.cms = options.cms;
        this.sportDataGateway = options.sportDataGatewayRetriever;
        this.statisticsProvider = options.statisticsProvider;
        this.betSlipUtil = options.utils.betSlipUtil;
        this.navHandler = options.navHandler;

        this.eventsManager = new EventsManager();
        this.timeControl = options.timeControl;
        this.currentTimeZone = this.timeControl.getCurrentTimeZone();
        this.currentOddStyle = OddStyles[options.currentOddStyleIndex];
        this.timeInstance = Time.getInstance;

        let timeProvider = { getServerDate: this.timeControl.getServerTime, currentTimeZone: this.timeControl.getCurrentTimeZone().toString() };
        this.timeInstance.init(timeProvider);
        this.userInfo = options.userInfo;

        this.updateCollection = this.updateCollection.bind(this);
        this.onPersonalizationDataSuccess = this.onPersonalizationDataSuccess.bind(this);
        this.onGetEventsExtendedData = this.onGetEventsExtendedData.bind(this);
        this.onError = this.onError.bind(this);
        this.onRefreshSportContent = this.onRefreshSportContent.bind(this);

        this.userInfo.onLogin[options.blockUniqueId] = this.onRefreshSportContent;
        this.userInfo.onLogout[options.blockUniqueId] = this.onRefreshSportContent;

        this.deviceChangeDispatcher = options.deviceChangeDispatcher;

        this.dict = options.dict;

        this.disposeSportsDataFeed = () => {};

        this.disposeTimeZone = () => {};

        this.eventsExtendedData = [];
    }

    // Should activate main flow of the Facade, activate from outside
    activate() : void {
        // Observe this.sportDataGateway["sportsDataChangeFeed"] for changes and call "updateCollection" with changeFeed from Sports Data API

        this.updateTimeZone(this.timeControl.getCurrentTimeZone());

        this.disposeSportsDataFeed = observe(this.sportDataGateway, "sportsDataChangeFeed", (change) => {
            this.updateCollection(change.newValue);
        });

        this.disposeRemoveOfEvents = observe(this.sportDataGateway, "shouldRemoveEvents", (change) => {
            this.shouldRemoveEvents = change.newValue;
        });

        this.disposeTimeZone = observe(this.timeInstance, (change : IObjectDidChange) => {
            let changedObject = change.object as Time;
            change.name === "currentTimeZone" && this.updateTimeZone(changedObject.currentTimeZone);
        });

        this.getPersonalizationData();
        this.eventsManager.addEventListener("oddStyleChanged", this, this.updateOddStyleOfEvents, true);
        this.deviceChangeDispatcher && this.deviceChangeDispatcher.activate();
    }

    deactivate() : void {
        this.disposeSportsDataFeed();
        this.disposeTimeZone();
        this.sportDataGateway.deactivate();
        this.eventsManager.removeEventListener("oddStyleChanged", this);
        this.deviceChangeDispatcher && this.deviceChangeDispatcher.deactivate();
        this.AuthorizationToken = undefined;
    }

    getPersonalizationData() {

        this.pageMethods.GetPersonalizationEvents(this.cms.MaxNumberOfEventsPerBranch, this.cms.MaxNumberOfBranches,
            !!this.cms.IsBranchGroupingInForYouTab, !!this.cms.IsEnableForYoutab, this.onPersonalizationDataSuccess, this.onError)
    }

    onPersonalizationDataSuccess(data) {
        this.sportDataGateway.deactivate();
        this.personalizationDataResponse = JSON.parse(data) as IPersonalizationMethodResponse;
        let eventsExtenedDataParams = buildEventsExtendedDataParams(this.personalizationDataResponse);
        this.pageMethods.GetEventsExtendedData(eventsExtenedDataParams, this.onGetEventsExtendedData, this.onError)
        this.sportDataGateway.activate(this.personalizationDataResponse);
    }

    onGetEventsExtendedData(data) {
        let response = JSON.parse(data) as IEventsExtendedDataResponse;
        this.eventsExtendedData = response.EventsExtendedData;
    }

    onError(result) {
    }

    onRefreshSportContent() {
        this.sportDataGateway.deactivate();
        this.getPersonalizationData();
    }

    updateCollection(dataFeed: EventPushResponse | undefined) : void {
        this.dataCollection = dataFeed;
    }

    @action.bound updateTimeZone(newTime: number) {
        if (!isNaN(newTime)) {
            this.currentTimeZone = newTime;
        }
    }

    @action.bound updateOddStyleOfEvents(oddStyle: number) {
        if (!isNaN(oddStyle)) {
            this.currentOddStyle = OddStyles[oddStyle];
        }
    }
}
